/**
 * Coarse "N ago" formatter for epoch-ms timestamps. Kept in web-admin rather than packages/core
 * because it is presentation-only and no other app asks for it yet. `nowMs` is injected rather
 * than read from `Date.now()` so specs are deterministic.
 */
export function formatRelativeTime(epochMs: number | null, nowMs: number = Date.now()): string {
  if (epochMs == null) return "—";
  const deltaMs = nowMs - epochMs;
  if (deltaMs < 0) return "just now";
  const min = Math.floor(deltaMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * A bare duration in minutes, e.g. "90m", "2h 14m", "3d 4h". Distinct from
 * `formatRelativeTime`, which renders an *instant* relative to now; this renders
 * an interval, which is what the derivation popover's thresholds and ages are.
 */
export function formatDurationMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) return "—";
  const min = Math.round(minutes);
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const remMin = min % 60;
  if (h < 24) return remMin === 0 ? `${h}h` : `${h}h ${remMin}m`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH === 0 ? `${d}d` : `${d}d ${remH}h`;
}

/** Absolute rendering for the hover title, so the coarse relative label stays auditable. */
export function formatAbsoluteTime(epochMs: number | null): string {
  if (epochMs == null) return "never generated";
  return new Date(epochMs).toLocaleString();
}
